import React, { useState, useEffect, useRef, useCallback } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import { useSuperAdmin } from "../context/SuperAdminContext";
import TemplateModal from "./TemplateModal";
import MessageHistoryCard from "./MessageHistoryCard";
import { FilterPanel }     from "../components/WorkerOnboarding/FilterPanel";
import { useWorkerFilters } from "../hooks/useWorkerFilters";
import NotificationModal from '../components/NotificationModal';

const BASE_URL = process.env.REACT_APP_BASE_URL;
const PAGE_LIMIT = 20;

// ---------------------------------------------------------------------------
// Modal
// ---------------------------------------------------------------------------
const Modal = ({ isOpen, onClose, children, title }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] flex flex-col">
        <div className="p-4 border-b flex justify-between items-center">
          <h2 className="text-xl font-semibold">{title}</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">✕</button>
        </div>
        <div className="flex-1 overflow-hidden">{children}</div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// SuperAdminDashboard
// ---------------------------------------------------------------------------
const SuperAdminDashboard = () => {
  const navigate = useNavigate();

  // Pull everything from the same context the Onboarding page uses
  const {
    token,
    fetchLocations,
    fetchSkills,
    fetchCompaniesByLocations,
    fetchLastCallStatuses,
    fetchLastCallSubStatuses, 
    fetchSources,
  } = useSuperAdmin();

  const [showNotifModal, setShowNotifModal] = useState(false);
  const [notificationMode, setNotificationMode] = useState(false);

  // Redirect if not logged in
  useEffect(() => {
    if (!token) navigate("/lkart/superadmin/");
  }, [token, navigate]);

  // ── All filter state — identical to WorkerOnboardingPage ─────────────────
  const filters = useWorkerFilters({
    fetchLocations,           // GET /lenskart/locations?isActive=false
    fetchSkills,              // GET /lenskart/skills?isActive=false
    fetchCompaniesByLocations, // POST /onDemandCompanies/list-companies { locationIds }
    fetchLastCallStatuses, 
    fetchLastCallSubStatuses,
    fetchSources,   
  });

  const { buildDashboardParams, clearAllFilters } = filters;

  // ── Dashboard-specific state ──────────────────────────────────────────────
  const [filtersCollapsed,  setFiltersCollapsed]  = useState(false);
  const [filterApplied,     setFilterApplied]     = useState(false);
  const [applyingFilters,   setApplyingFilters]   = useState(false);
  const [downloadingCSV,    setDownloadingCSV]    = useState(false);
  const [sendingMessage,    setSendingMessage]    = useState(false);
  const [messageSent,       setMessageSent]       = useState(false);
  const [csvDownloaded,     setCsvDownloaded]     = useState(false);
  const [workers,           setWorkers]           = useState({ attendance: [], totalWorkers: -1 });
  const [showWorkersModal,  setShowWorkersModal]  = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [currentPage,       setCurrentPage]       = useState(1);
  const [totalPages,        setTotalPages]        = useState(1);
  const [loadingMore,       setLoadingMore]       = useState(false);
  const [csvUploadId, setCsvUploadId] = useState("");

  const modalContentRef   = useRef(null);
  const messageHistoryRef = useRef(null);
  const hasWorkers        = workers.totalWorkers > 0;

  // ── Reset results whenever any filter changes ─────────────────────────────
  const resetResults = useCallback(() => {
    setFilterApplied(false);
    setWorkers({ attendance: [], totalWorkers: -1 });
  }, []);

  // ── Build query string: shared filter params ────────────────────────────
  const buildQueryString = useCallback(
    (extra = {}) => {
      const params = buildDashboardParams();
      return new URLSearchParams({ ...params, ...extra }).toString();
    },
    [buildDashboardParams]
  );

  const handleUploadCsv = useCallback(()=>{
    navigate("/lkart/superadmin/upload-csv");
  })

  // ── Fetch workers (dashboard-specific API — unchanged from original) ───────
  const fetchWorkers = useCallback(
    async (page = 1, append = false) => {
      try {
        // Build payload using the same builder as Onboarding page
        const payload = filters.buildOnboardingPayload(page);

        if (csvUploadId && csvUploadId.trim()) {
        payload.uploadId = csvUploadId.trim();
      }
        
        console.log("📤 Sending to /on-boarding/filters:", JSON.stringify(payload, null, 2));
        
        // Use the same API endpoint as WorkerOnboardingPage
       const response = await fetch(`${BASE_URL}/on-boarding/filters`, {
  method: "POST",
  headers: {
    "Authorization": `Bearer ${localStorage.getItem("superadmintoken")}`,
    "Content-Type": "application/json",
  },
  body: JSON.stringify({ ...payload, filterSource: 'send_message' }),
});

        if (!response.ok) {
          throw new Error(`API error: ${response.status}`);
        }

        const data = await response.json();
        
        // Extract workers and total count (same response structure as Onboarding page)
        const workersList = Array.isArray(data?.data) ? data.data 
                          : Array.isArray(data?.workers) ? data.workers 
                          : Array.isArray(data?.results) ? data.results 
                          : [];
        
        const total = data?.total ?? data?.count ?? workersList.length;
        
        const transformed = {
          attendance: workersList,
          totalWorkers: total,
          pagination: {
            totalPages: Math.ceil(total / PAGE_LIMIT),
            currentPage: page,
            totalItems: total,
          },
        };

        setMessageSent(false);
        setCsvDownloaded(false);

        if (append) {
          setWorkers((prev) => ({
            ...transformed,
            attendance: [...prev.attendance, ...transformed.attendance],
          }));
        } else {
          setWorkers(transformed);
        }

        setTotalPages(transformed.pagination.totalPages);
        setCurrentPage(transformed.pagination.currentPage);
        
        return transformed;
      } catch (err) {
        console.error("Error fetching workers:", err);
        toast.error(err.message || "An error occurred while fetching data.");
        throw err;
      }
    },
    [filters, csvUploadId] // Remove buildQueryString dependency
  );

  const handleSendNotification = useCallback(async (payload) => {
  try {
    const response = await fetch(`${BASE_URL}/on-boarding/filters`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('superadmintoken')}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...filters.buildOnboardingPayload(currentPage),
        ...payload,                      
      }),
    });
    if (!response.ok) throw new Error('Failed to send notification');
    toast.success('Notification sent successfully');
    return { success: true };
  } catch (err) {
    toast.error(err.message);
    return { success: false };
  }
}, [filters, currentPage]);

  // ── Apply filters ─────────────────────────────────────────────────────────
  const handleFilterApply = useCallback(async () => {
    const {
      selectedLocations, selectedSkills,
      selectedWorkerTypes, selectedGenders, selectedSources,
    } = filters;

    if (
      selectedLocations.length   === 0 ||
      selectedSkills.length      === 0 ||
      selectedWorkerTypes.length === 0 ||
      selectedGenders.length     === 0 ||
      selectedSources.length     === 0
    ) {
      toast.error("Please fill all required fields and select at least one option for each filter.");
      return;
    }

    setApplyingFilters(true);
    try {
      await fetchWorkers(1, false);
      setFilterApplied(true);
      toast.success("Filters applied successfully.");
    } catch (err) {
      console.error("Error applying filters:", err);
      toast.error(err.message || "An error occurred while fetching data.");
    } finally {
      setApplyingFilters(false);
    }
  }, [filters, fetchWorkers]);

  // ── Download CSV ──────────────────────────────────────────────────────────
  const handleDownloadCSV = useCallback(async () => {
    if (!filterApplied) { 
      toast.error("Please apply filters first."); 
      return; 
    }
    if (!hasWorkers) { 
      toast.error("No workers available to download."); 
      return; 
    }

    try {
      setDownloadingCSV(true);
      
      // Build payload with download flag
      const payload = {
        ...filters.buildOnboardingPayload(currentPage),
        downloadCsv: true,  // This triggers CSV download on the backend
      };
      
      const response = await fetch(`${BASE_URL}/on-boarding/filters`, {
  method: "POST",
  headers: {
    "Authorization": `Bearer ${localStorage.getItem("superadmintoken")}`,
    "Content-Type": "application/json",
  },
  body: JSON.stringify({ ...payload, filterSource: 'send_message' }),
});

      if (!response.ok) {
        throw new Error("Failed to download CSV");
      }

      // Get the blob from response
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      
      const loc = filters.selectedLocations;
      const locationLabel = loc.length === 1 ? loc[0].replace(/\s+/g, "_") : `${loc.length}_locations`;
      
      link.href = url;
      link.setAttribute("download", `workers_${locationLabel}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      setCsvDownloaded(true);
      toast.success("Worker CSV downloaded successfully");
    } catch (err) {
      console.error("Error downloading CSV:", err);
      toast.error(err.message || "Failed to export data");
    } finally {
      setDownloadingCSV(false);
    }
  }, [filterApplied, hasWorkers, filters, currentPage]);

  // ── Send WhatsApp message ─────────────────────────────────────────────────
  const handleSendMessage = useCallback(
    async (
      selectedTemplate, variableValues, msgGenders,
      msgSources, platform, mediaData = null, buttonsData = null
    ) => {
      if (!filterApplied) { 
        toast.error("Please apply filters first."); 
        return { success: false }; 
      }
      if (!hasWorkers) { 
        toast.error("No workers available."); 
        return { success: false }; 
      }

      try {
        setSendingMessage(true);
        
        // Build payload with WhatsApp flags
        const payload = {
          ...filters.buildOnboardingPayload(currentPage),
          sendWhatsApp: true,
          templateId: selectedTemplate._id,
          templateName: selectedTemplate.templateName,
          dynamicVariables: JSON.stringify(Object.values(variableValues)),
          platform,
          // ...(csvUploadId && { uploadId: csvUploadId })
        };

        if (csvUploadId && csvUploadId.trim()) {
        payload.uploadId = csvUploadId.trim();
      }
        
        // Add optional media/buttons
        if (mediaData) {
          payload.mediaType = mediaData.type;
          payload.mediaURL = mediaData.url;
        }
        if (buttonsData) {
          payload.buttons = JSON.stringify(buttonsData);
        }
        
        const response = await fetch(`${BASE_URL}/on-boarding/filters`, {
  method: "POST",
  headers: {
    "Authorization": `Bearer ${localStorage.getItem("superadmintoken")}`,
    "Content-Type": "application/json",
  },
  body: JSON.stringify({ ...payload, filterSource: 'send_message' }),
});

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(errorText || "Failed to send messages");
        }

        // Check content type for response handling
        const contentType = response.headers.get("content-type");
        if (contentType?.includes("application/json")) {
          await response.json();
        } else {
          await response.text();
        }

        setMessageSent(true);
        toast.success("WhatsApp messages sent successfully");
        messageHistoryRef.current?.refreshHistory();
        return { success: true };
      } catch (err) {
        console.error("Error sending messages:", err);
        toast.error(err.message || "Failed to send messages");
        return { success: false, error: err.message };
      } finally {
        setSendingMessage(false);
      }
    },
    [filterApplied, hasWorkers, filters, currentPage, csvUploadId]
  );

  // ── Send App Notification ─────────────────────────────────────────────────
  const handleSendAppNotification = useCallback(
    async (payload) => {
      if (!filterApplied) { 
        toast.error("Please apply filters first."); 
        return { success: false }; 
      }
      if (!hasWorkers) { 
        toast.error("No workers available."); 
        return { success: false }; 
      }

      try {
        setSendingMessage(true);
        
        // Build the request payload
        const {
  notificationTitle,
  notificationBody,
  validTillDate,
  genders,
  sources,
  templateId,
  templateName,
  dynamicVariables,
  useButtons,
  button1,
  button2,
} = payload;
        
        const requestPayload = {
  filterSource: 'send_notification',
  hasAppToken: true,
  notificationBody,
  validTillDate,
  useButtons: useButtons || false,
  templateId,
  templateName,
  dynamicVariables: JSON.parse(dynamicVariables || '[]'),
};

if (notificationTitle && notificationTitle.trim()) {
  requestPayload.notificationTitle = notificationTitle.trim();
}

if (useButtons) {
  requestPayload.button1 = button1;
  requestPayload.button2 = button2;
}
        
        // Add filters
        if (genders && genders.length > 0) {
          requestPayload.genders = genders;
        }
        if (sources && sources.length > 0) {
          requestPayload.sources = sources;
        }
        
        const response = await fetch(`${BASE_URL}/on-boarding/filters`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('superadmintoken')}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ...filters.buildOnboardingPayload(currentPage),
            ...requestPayload,
          }),
        });
        
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(errorText || 'Failed to send notification');
        }
        
        setMessageSent(true);
        toast.success('App notifications sent successfully');
        messageHistoryRef.current?.refreshHistory();
        return { success: true };
      } catch (err) {
        console.error('Error sending notification:', err);
        toast.error(err.message || 'Failed to send notification');
        return { success: false, error: err.message };
      } finally {
        setSendingMessage(false);
      }
    },
    [filterApplied, hasWorkers, filters, currentPage]
  );

  // ── Infinite scroll in workers modal ─────────────────────────────────────
  const handleScroll = useCallback(
    async (e) => {
      const { scrollTop, scrollHeight, clientHeight } = e.target;
      if (
        scrollHeight - scrollTop <= clientHeight * 1.5 &&
        !loadingMore &&
        currentPage < totalPages
      ) {
        setLoadingMore(true);
        await fetchWorkers(currentPage + 1, true);
        setLoadingMore(false);
      }
    },
    [loadingMore, currentPage, totalPages, fetchWorkers]
  );

  const goToCsvUploadPage = useCallback(()=>{
    navigate("/lkart/superadmin/upload-csv");
  })

  useEffect(() => {
    const el = modalContentRef.current;
    if (el) {
      el.addEventListener("scroll", handleScroll);
      return () => el.removeEventListener("scroll", handleScroll);
    }
  }, [handleScroll]);

  // ── Derived ───────────────────────────────────────────────────────────────
  const canApply =
    filters.selectedLocations.length   > 0 &&
    filters.selectedSkills.length      > 0 &&
    filters.selectedWorkerTypes.length > 0 &&
    filters.selectedGenders.length     > 0 &&
    filters.selectedSources.length     > 0;

  const handleClearAll = useCallback(() => {
    clearAllFilters(resetResults);
    toast.success("All filters cleared");
  }, [clearAllFilters, resetResults]);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-8xl mx-auto px-6 py-8 mt-4">
        <div className="bg-white p-6 rounded-xl shadow-md">

          <h2 className="text-2xl font-semibold text-gray-800 text-center mb-4">
            Send Message
          </h2>

          {/* ── Collapsible filter panel — identical shell to WorkerOnboardingPage ── */}
          <div className="border border-gray-200 rounded-lg mb-6">

            {/* Toggle header */}
            <button
              onClick={() => setFiltersCollapsed(!filtersCollapsed)}
              className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors rounded-t-lg"
            >
              <div className="flex items-center gap-2">
                <svg
                  className={`w-5 h-5 text-gray-500 transition-transform ${filtersCollapsed ? "" : "rotate-180"}`}
                  fill="none" stroke="currentColor" viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
                <span className="font-medium text-gray-700">Filters</span>
                <span className="text-xs text-gray-400">
                  {filtersCollapsed ? "(click to expand)" : "(click to collapse)"}
                </span>
              </div>

              {/* Collapsed: quick-action buttons */}
              {filtersCollapsed && (
                <div className="flex items-center gap-3">
                  {filterApplied && (
                    <div className="flex items-center gap-2 bg-white px-3 py-1 rounded-full border border-gray-200">
                      <span className="text-sm font-semibold text-gray-700">
                        Total: <span className="text-indigo-600">{workers.totalWorkers}</span>
                      </span>
                    </div>
                  )}
                  <button
                    onClick={(e) => { e.stopPropagation(); handleClearAll(); }}
                    className="bg-gray-500 text-white px-4 py-1.5 rounded-lg hover:bg-gray-600 text-sm font-medium"
                  >
                    Clear All Filters
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleFilterApply(); }}
                    disabled={applyingFilters || !canApply}
                    className="bg-indigo-500 text-white px-4 py-1.5 rounded-lg hover:bg-indigo-600 disabled:opacity-50 text-sm font-medium"
                  >
                    {applyingFilters ? "Applying..." : "Apply Filters"}
                  </button>
                </div>
              )}

              {/* Expanded: total count pill */}
              {!filtersCollapsed && filterApplied && (
                <div className="flex items-center gap-2 bg-white px-3 py-1 rounded-full border border-gray-200">
                  <span className="text-sm font-semibold text-gray-700">
                    Total: <span className="text-indigo-600">{workers.totalWorkers}</span>
                  </span>
                </div>
              )}
            </button>

            {/* Expanded body */}
            {!filtersCollapsed && (
              <div className="p-4 border-t border-gray-200">

                {/* Full shared filter grid — all filters, same as WorkerOnboardingPage */}
                <FilterPanel
                  filters={filters}
                  onReset={resetResults}
                  showOnboardingFilters={true}
                />

                <div className="mt-6 pt-4 border-t border-gray-200">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                    <div className="w-full max-w-[420px]">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        CSV Upload ID (Optional)
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          value={csvUploadId}
                          onChange={(e) => setCsvUploadId(e.target.value)}
                          placeholder="Enter CSV Upload ID"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 pr-10"
                        />
                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              const text = await navigator.clipboard.readText();
                              setCsvUploadId(text);
                              toast.success("ID pasted from clipboard");
                            } catch (err) {
                              toast.error("Unable to read clipboard. Please paste manually.");
                            }
                          }}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-indigo-600 transition-colors"
                          title="Paste from clipboard"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" 
                            />
                          </svg>
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Provide the upload ID from <button className="font-semibold text-indigo-600" onClick={goToCsvUploadPage}>CSV Upload</button> to send messages
                      </p>
                    </div>
                  </div>
                </div>

                {/* Action bar */}
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6 pt-4 border-t border-gray-200">
                  <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
                    {filterApplied && (
                      <>
                        <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg border border-gray-300">
                          <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                              d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                            />
                          </svg>
                          <span className="text-sm font-semibold text-gray-700">
                            Total Workers: <span className="text-indigo-600">{workers.totalWorkers}</span>
                          </span>
                        </div>

                        <button
                          onClick={() => setShowWorkersModal(true)}
                          className="bg-purple-500 text-white px-4 py-2 rounded-lg hover:bg-purple-600 text-sm font-medium"
                        >
                          View Workers
                        </button>

                        {/* WhatsApp Message Button */}
                        <button
                          onClick={() => {
                            setNotificationMode(false);
                            setShowTemplateModal(true);
                          }}
                          disabled={sendingMessage || messageSent || !hasWorkers}
                          className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 disabled:opacity-50 text-sm font-medium"
                        >
                          {sendingMessage ? "Processing..." : messageSent ? "Message Sent" : "Send WhatsApp"}
                        </button>

                        {/* App Notification Button */}
                        <button
                          onClick={() => {
                            setNotificationMode(true);
                            setShowTemplateModal(true);
                          }}
                          disabled={sendingMessage || messageSent || !hasWorkers}
                          className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 disabled:opacity-50 text-sm font-medium"
                        >
                          {sendingMessage ? "Processing..." : messageSent ? "Sent" : "Send App Notification"}
                        </button>

                        <button onClick={() => setShowNotifModal(true)}>
                          <span className="bg-yellow-500 text-white px-4 py-2 rounded-lg hover:bg-yellow-600 text-sm font-medium">
                            Create Notification Template
                          </span>
                        </button>

                      </>
                    )}
                  </div>

                  <div className="flex gap-3 w-full sm:w-auto">
                    <button
                      onClick={handleClearAll}
                      className="bg-gray-500 text-white px-6 py-2.5 rounded-lg hover:bg-gray-600 font-medium"
                    >
                      Clear All Filters
                    </button>
                    <button
                      onClick={handleFilterApply}
                      disabled={applyingFilters || !canApply}
                      className="bg-indigo-500 text-white px-6 py-2.5 rounded-lg hover:bg-indigo-600 disabled:opacity-50 font-medium"
                    >
                      {applyingFilters ? "Applying..." : "Apply Filters"}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Workers modal */}
          <Modal isOpen={showWorkersModal} onClose={() => setShowWorkersModal(false)} title="Workers List">
            <div
              ref={modalContentRef}
              className="overflow-y-auto p-4"
              style={{ maxHeight: "calc(80vh - 4rem)" }}
            >
              <div className="space-y-4">
                {workers.attendance.map((worker, index) => (
                  <div
                    key={worker.id || worker.workerId || index}
                    className="p-4 border rounded-lg shadow-sm hover:shadow-md transition-shadow"
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <h3 className="font-medium">{worker.name}</h3>
                        <p className="text-sm text-gray-600">{worker.workerCode}</p>
                        <p className="text-sm text-gray-600">{worker.phone}</p>
                      </div>
                    </div>
                  </div>
                ))}
                {loadingMore && (
                  <div className="text-center py-4"><p>Loading more workers...</p></div>
                )}
                {workers.attendance.length === 0 && filterApplied && (
                  <div className="text-center py-4">
                    <p>No workers found with the selected filters.</p>
                  </div>
                )}
              </div>
            </div>
          </Modal>

          <TemplateModal
            isOpen={showTemplateModal}
            onClose={() => {
              setShowTemplateModal(false);
              setNotificationMode(false);
            }}
            onConfirm={notificationMode ? handleSendAppNotification : handleSendMessage}
            selectedGenders={filters.selectedGenders}
            selectedSources={filters.selectedSources}
            mode={notificationMode ? 'notification' : 'whatsapp'}
          />

          <NotificationModal
            isOpen={showNotifModal}
            onClose={() => setShowNotifModal(false)}
            onConfirm={handleSendNotification}
            apiBaseUrl={BASE_URL}
          />
        </div>
      </div>

      <div className="max-w-8xl mx-auto px-6 mt-6">
        <MessageHistoryCard ref={messageHistoryRef} />
      </div>

      <ToastContainer
        position="top-right" autoClose={3000} limit={1} hideProgressBar={false}
        newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover theme="light"
        toastClassName={() =>
          "relative bg-white text-black px-4 py-3 rounded-md shadow-lg border border-gray-300 " +
          "w-[98%] max-w-[98%] sm:w-72 sm:max-w-[300px] overflow-hidden"
        }
        bodyClassName={() => "flex items-center"}
        progressClassName="bg-blue-500 h-1"
        className="w-[98%] max-w-[98%] sm:w-72 sm:max-w-[300px] right-4 top-4"
      />
    </div>
  );
};

export default SuperAdminDashboard;